/**
 * Validated Sabotage Stat Mapping
 * 
 * Maps sabotage stat references to actual `player_game_stats` columns,
 * preventing silent failures from typos or invalid field names.
 */

// Columns in the player_game_stats table that can be targeted by stat_multiplier sabotages
export const VALID_STAT_COLUMNS = [
  'strikeouts',
  'errors',
  'gidp',
  'caught_stealing',
  'earned_runs',
  'walks_allowed',
  'wild_pitches',
  'blown_saves',
  'hit_batsmen',
  'home_runs_allowed',
  'balks',
  'passed_balls',
] as const;

export type ValidStatColumn = typeof VALID_STAT_COLUMNS[number];

// Human-readable labels for each stat column
export const STAT_COLUMN_LABELS: Record<ValidStatColumn, string> = {
  strikeouts: 'Strikeouts (K)',
  errors: 'Errors (E)',
  gidp: 'Double Plays (GIDP)',
  caught_stealing: 'Caught Stealing (CS)',
  earned_runs: 'Earned Runs (ER)',
  walks_allowed: 'Walks (BB)',
  wild_pitches: 'Wild Pitches (WP)',
  blown_saves: 'Blown Saves (BS)',
  hit_batsmen: 'Hit Batsmen (HBP)',
  home_runs_allowed: 'Home Runs Allowed (HR)',
  balks: 'Balks (BK)',
  passed_balls: 'Passed Balls (PB)',
};

// Scoring category each stat belongs to
export const STAT_COLUMN_CATEGORY: Record<ValidStatColumn, 'batting' | 'pitching' | 'fielding' | 'baserunning'> = {
  strikeouts: 'batting',
  gidp: 'batting',
  errors: 'fielding',
  passed_balls: 'fielding',
  caught_stealing: 'baserunning',
  earned_runs: 'pitching',
  walks_allowed: 'pitching',
  wild_pitches: 'pitching',
  blown_saves: 'pitching',
  hit_batsmen: 'pitching',
  home_runs_allowed: 'pitching',
  balks: 'pitching',
};

// Default trash point values per stat (used by scoring engine)
export const STAT_DEFAULT_POINTS: Record<ValidStatColumn, number> = {
  strikeouts: 2,
  gidp: 4,
  errors: 3,
  passed_balls: 3,
  caught_stealing: 3,
  earned_runs: 2,
  walks_allowed: 1.5,
  wild_pitches: 3,
  blown_saves: 6,
  hit_batsmen: 2,
  home_runs_allowed: 3,
  balks: 5,
};

// Validate a stat column name at runtime
export function isValidStatColumn(col: string): col is ValidStatColumn {
  return VALID_STAT_COLUMNS.includes(col as ValidStatColumn);
}

// ─── Sabotage Effect Types ──────────────────────────────────────────
export const VALID_EFFECT_TYPES = [
  'score_modifier',
  'score_multiplier',
  'stat_multiplier',
  'roster_restriction',
  'info',
  'timer_reduction',
  'fake_injury',
  'order_reversal',
  'pick_skip',
  'guess_who',
  'karma',
  'targeted_stat_multiplier',
  'pitcher_swap',
  'self_weight_modifier',
  'self_stat_nullifier',
] as const;

export type ValidEffectType = typeof VALID_EFFECT_TYPES[number];

export function isValidEffectType(t: string): t is ValidEffectType {
  return VALID_EFFECT_TYPES.includes(t as ValidEffectType);
}

// ─── Rarity Tiers ───────────────────────────────────────────────────
export const SABOTAGE_RARITIES = ['common', 'rare', 'legendary'] as const;
export type SabotageRarity = typeof SABOTAGE_RARITIES[number];

export interface RarityConfig {
  label: string;
  /** Max uses per team per period (0 = unlimited) */
  weeklyLimit: number;
  /** Limit period: 'week' for common/rare, 'season' or 'draft' for legendary */
  limitPeriod: 'week' | 'season' | 'draft';
  /** Whether this sabotage affects all teams globally */
  isGlobal: boolean;
  /** Border / badge color class (uses design tokens) */
  borderClass: string;
  badgeClass: string;
  glowClass: string;
}

export const RARITY_CONFIG: Record<SabotageRarity, RarityConfig> = {
  common: {
    label: 'Common',
    weeklyLimit: 1,
    limitPeriod: 'week',
    isGlobal: false,
    borderClass: 'border-rarity-common/40',
    badgeClass: 'bg-rarity-common/20 text-rarity-common border-rarity-common/40',
    glowClass: '',
  },
  rare: {
    label: 'Rare',
    weeklyLimit: 0,
    limitPeriod: 'week',
    isGlobal: false,
    borderClass: 'border-rarity-rare/50',
    badgeClass: 'bg-rarity-rare/20 text-rarity-rare border-rarity-rare/50',
    glowClass: 'shadow-[0_0_12px_hsl(var(--rarity-rare-glow)/0.3)]',
  },
  legendary: {
    label: 'Legendary',
    weeklyLimit: 1,
    limitPeriod: 'season',
    isGlobal: true,
    borderClass: 'border-rarity-legendary/60',
    badgeClass: 'bg-rarity-legendary/20 text-rarity-legendary border-rarity-legendary/60',
    glowClass: 'shadow-[0_0_20px_hsl(var(--rarity-legendary-glow)/0.4)]',
  },
};

// ─── Type-Safe Sabotage Catalog Entry ───────────────────────────────
export interface SabotageCatalogEntry {
  name: string;
  icon: string;
  description: string;
  effect_type: ValidEffectType;
  effect_config: SabotageEffectConfig;
  color: 'damage' | 'primary' | 'success';
  /** Which context this sabotage applies to */
  context: 'matchup' | 'draft' | 'both';
  /** Rarity tier — affects styling and weekly limits */
  rarity: SabotageRarity;
}

export type SabotageEffectConfig =
  | { flat_points: number }
  | { multiplier: number }
  | { stat_column: ValidStatColumn; multiplier: number }
  | { restriction: string; lock_hours?: number; flat_points?: number }
  | { reveal: string; duration_hours: number }
  | { reduction_factor: number; min_seconds: number }
  | { player_id?: string; top_n: number }
  | { min_round?: number }
  | { duration_picks: number }
  | { hit_count_threshold?: number }
  | { stat_columns: string[]; multiplier: number; target: 'opponent_pitchers' | 'opponent_hitters' }
  | { target: 'opponent' | 'self' }  // pitcher_swap: matches VALID_PITCHER_SWAP_TARGETS in apply-sabotage
  | { stat_columns: string[]; reduction_pct: number; target: 'self_hitters' | 'self_pitchers' }
  | { stat_column: string; target: 'self_hitters' }
  | Record<string, never>; // empty config
